import React, { useCallback, useMemo } from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { DialogContent } from "components/MaterialDialog";
import { Settings } from "constants/settings";
import { getSetting } from "selectors";
import { setSetting } from "actions";
import { PenRangeFormValues } from "components/Filter/Form/PenRange/processData";

import { SchemaOf } from "yup/lib";
import * as Yup from "yup";

import { getFilterConf } from "components/Filter/filterIds";

import { FilterConf } from "components/Filter/types";
import {
  FilterSetting,
  FilterSettings,
  FilterState,
} from "reducers/settings/types";
import { ModalActions } from "./DialogActions";

interface ModalContentProps {
  filterIds: string[];
  onClose: () => void;
}

interface FormValues {
  [key: string]: PenRangeFormValues;
}

function applyFilters(
  filterIds: string[],
  filterSettings: FilterSettings,
): FormValues {
  return filterIds.reduce((acc: FormValues, filterId: string) => {
    const filterConf = getFilterConf(filterId);
    if (!filterConf) {
      return acc;
    }

    // retrieve the existing Filter setting from the State for this specific filter
    const filterSetting: FilterSetting = filterSettings.find(
      (fs: FilterSetting) => fs.id === filterId,
    );

    acc[filterId] = filterConf.getFormData(filterId, filterSetting);

    return acc;
  }, {});
}

function getFilterSettings(
  filterIds: string[],
  formData: FormValues,
  filterSettings: FilterSettings,
): FilterSettings {
  return filterIds.reduce(
    (acc: FilterSettings, filterId: string) => {
      const filterConf: FilterConf = getFilterConf(filterId);
      if (!filterConf) {
        return acc;
      }

      const formFilterData = formData[filterId];

      if (!filterConf.getIsActive(formFilterData)) {
        return acc;
      }

      // Convert the data in the form to a standard filter configuration
      const filterSetting: FilterSetting = filterConf.getFilterSetting(
        filterId,
        formFilterData,
      );

      const filterSettingIndex = acc.findIndex(
        filter => filter.id === filterId,
      );

      if (filterSettingIndex >= 0) {
        // replace the old one with the new one!
        acc.splice(filterSettingIndex, 1, filterSetting);
      } else {
        // The filter is not yet in the applied filters, add it to the end
        acc.push(filterSetting);
      }
      return acc;
    },
    [...filterSettings],
  );
}

type SchemaKey = keyof FormValues;
type SchemaValue<T> = { [key: SchemaKey]: SchemaOf<T> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup schema object
function applyValidation<T>(filterIds: string[]): any {
  const validationShape: SchemaValue<T> = filterIds.reduce(
    (acc, filterId: string) => {
      const filterConf: FilterConf = getFilterConf(filterId);
      if (!filterConf) {
        return acc;
      }

      acc[filterId] = filterConf.getValidationSchema(filterId);

      return acc;
    },
    {},
  );

  return Yup.object().shape(validationShape);
}

export function ModalContent(props: ModalContentProps): React.JSX.Element {
  const { filterIds, onClose } = props;

  const dispatch = useDispatch();
  const { filterSettings = [] }: FilterState =
    useSelector(getSetting(Settings.filter)) || {};

  const onSubmit = useCallback(
    (values: FormValues) => {
      const newFilters: FilterSettings = getFilterSettings(
        filterIds,
        values,
        filterSettings,
      );

      dispatch(setSetting(Settings.filter, { filterSettings: newFilters }));
      onClose();
    },
    [dispatch, filterIds, filterSettings, onClose],
  );

  const initialValues: FormValues = useMemo(
    () => applyFilters(filterIds, filterSettings),
    [filterIds, filterSettings],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup schema object
  const validationSchema: any = useMemo(
    () => applyValidation(filterIds),
    [filterIds],
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <DialogContent>
          {filterIds.map(filterId => {
            const { filterForm: FilterForm, filterFormExtraProps = {} } =
              getFilterConf(filterId);
            return (
              <FilterForm
                namespace={filterId}
                key={filterId}
                id={filterId}
                {...filterFormExtraProps}
              />
            );
          })}
        </DialogContent>
        <ModalActions
          filterIds={filterIds}
          onClose={onClose}
          readOnly={false}
        />
      </Form>
    </Formik>
  );
}
